import cn from 'classnames'
import Avatar from 'ext-ui/components/Avatar/Avatar'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import RoundCard from 'ext-ui/components/RoundCard/RoundCard'
import MagicalAIIcon from 'ext-ui/svg/icon/MagicalAIIcon'
import React from 'react'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

interface PricingTierIconText {
  icon: React.ReactElement
  text: string
}

interface PricingTierCardContent {
  pricingCardAvatar: string
  forWhichBusinesses: string
  planTitle: string
  pricingCardPerUserPrice: React.ReactElement
  perUserText?: string
  priceDetailsText: string
  actualPriceText: string | undefined
  buttonText: string
  promoText?: string
  strikeOutPrice?: number
  buttonLoadingState?: boolean
  getStartedHeading?: string
}

interface PricingTierCardProps {
  onClickPlanButton: () => void
  tierFeatures: PricingTierIconText[]
  aiPowered?: boolean
  variant?: 'highlight'
  content: PricingTierCardContent
}

const PricingTierCard = (props: PricingTierCardProps): React.ReactElement => {
  const {
    content: pricingTierCardContent,
    onClickPlanButton,
    tierFeatures,
    aiPowered,
    variant,
  } = props
  const {
    pricingCardAvatar,
    forWhichBusinesses,
    planTitle,
    pricingCardPerUserPrice,
    perUserText,
    priceDetailsText,
    actualPriceText,
    buttonText,
    getStartedHeading,
    promoText,
    strikeOutPrice,
    buttonLoadingState,
  } = pricingTierCardContent
  const translate = useTranslate()

  return (
    <RoundCard
      className={cn('PricingCard-component flex-column gap-8', {
        'PricingCard-component-pro': variant === 'highlight',
      })}
      variant={variant}
    >
      {variant === 'highlight' && promoText && (
        <Pill className="PricingCard-pro-promoText" color="saas">
          {translate(promoText)}
        </Pill>
      )}
      <div className="flex-row align-top justify-start gap-3">
        <Avatar
          className="PricingCard-Avatar PricingCard-Avatar-enterprise"
          imageSrc={pricingCardAvatar}
        />
        <div className="PricingCard-name">
          <p className="PricingCard-name-planTitle">{translate(planTitle)}</p>
          <p className="PricingCard-name-topText">
            {translate(forWhichBusinesses)}
          </p>
        </div>
      </div>
      <div className="flex-column gap-3">
        <div className="flex-row align-center gap-3">
          {strikeOutPrice && (
            <div className="PricingCard-strikeout">
              <Crncy
                amount={strikeOutPrice}
                currency={'USD'}
                maximumFractionDigits={0}
              />
            </div>
          )}
          <div className="flex-row align-baseline">
            {pricingCardPerUserPrice}
            {perUserText ? (
              <p className="PricingCard-per-userValue">
                {translate(perUserText)}
              </p>
            ) : (
              <div className="PricingCard-per-userValue"> </div>
            )}
          </div>
        </div>
        <div className="PricingCard-price-details gap-1">
          <p className="PricingCard-price-details-text">
            {translate(priceDetailsText)}
          </p>
          {typeof actualPriceText === 'string' && (
            <p className="PricingCard-price-details-text">
              {translate(actualPriceText)}
            </p>
          )}
        </div>
      </div>
      <Button
        variant={variant === 'highlight' ? 'primary' : 'secondary'}
        className="Button-cta"
        onClick={onClickPlanButton}
        loading={buttonLoadingState}
        data-testid={`pricing-tier-button-${planTitle}`}
      >
        {buttonText}
      </Button>
      <div className="flex-column gap-6">
        {getStartedHeading && (
          <p className="PricingCard-getStarted-heading">
            {translate(getStartedHeading)}
          </p>
        )}
        {aiPowered && (
          <div className="PricingCard-AI-Powered flex-row align-center gap-1">
            <div className="flex-column align-center">
              <MagicalAIIcon />
            </div>
            <div className="PricingCard-AI-Powered-text">
              {translate('AI POWERED')}
            </div>
          </div>
        )}
        <div className="flex-column gap-2">
          {tierFeatures.map((feature, i) => (
            <div className="flex-row align-center" key={i}>
              <div className="PricingCard-checklist-item-icon">
                {feature.icon}
              </div>
              <div className="PricingCard-checklist-item-text">
                {translate(feature.text)}
              </div>
            </div>
          ))}
        </div>
        {/* PLACEHOLDER: LINKTEXT COMPONENT IN PR */}
        <a
          href="https://www.paywithextend.com/pricing"
          className="LinkText"
          target="_blank"
          rel="noopener noreferrer"
        >
          {translate('View all features')}
        </a>
      </div>
    </RoundCard>
  )
}

export default PricingTierCard
